import React, { useEffect } from "react";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { ListChecks, Shield, Search, BadgeDollarSign, Users, Image, BookOpen } from "lucide-react";
import { addSchemaToHead } from "@/utils/schemaMarkup";

const CollectingGuide: React.FC = () => {
	useSEO({
		title: "Collecting Florida Highwaymen Art — Complete Guide",
		description: "Learn how to collect Florida Highwaymen art: authentication, pricing, condition, provenance, and where to buy.",
		keywords: "Collecting Florida Highwaymen, Highwaymen authentication, Highwaymen prices, provenance, condition",
		canonicalUrl: "/collecting-florida-highwaymen-art",
		ogType: 'article',
		ogImage: "/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png"
	});

	useEffect(() => {
		const howTo = {
			"@context": "https://schema.org",
			"@type": "HowTo",
			"name": "How to Collect Florida Highwaymen Art",
			"description": "Step-by-step guide to collecting authentic Florida Highwaymen paintings.",
			"step": [
				{"@type": "HowToStep", "name": "Study the movement", "text": "Learn the history and artists."},
				{"@type": "HowToStep", "name": "Authenticate works", "text": "Evaluate signature, materials, style, and provenance."},
				{"@type": "HowToStep", "name": "Assess condition", "text": "Check for restoration, surface issues, and structural integrity."},
				{"@type": "HowToStep", "name": "Research pricing", "text": "Compare past sales and market benchmarks."},
				{"@type": "HowToStep", "name": "Buy confidently", "text": "Use reputable auctions and request appraisals when needed."}
			]
		};
		addSchemaToHead(howTo);
	}, []);

	return (
		<Layout>
			<div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
				<div className="container mx-auto px-4 py-12">
					<div className="max-w-5xl mx-auto">
						<div className="text-center mb-12">
							<h1 className="text-4xl md:text-5xl font-light text-white tracking-wide mb-4">Collecting Florida Highwaymen Art</h1>
							<p className="text-white/70 font-light text-lg">An expert guide to authentication, condition, pricing, and buying</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><BookOpen className="h-5 w-5 text-emerald-400" /> Study the Movement</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Start with the <Link to="/florida-highwaymen" className="text-emerald-300 underline">Florida Highwaymen guide</Link> to understand the history, artists, and styles.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><Shield className="h-5 w-5 text-emerald-400" /> Authentication</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Review signatures, materials (Upson board, Masonite), brushwork, and provenance. When uncertain, request an <Link to="/florida-highwaymen-appraisal" className="text-emerald-300 underline">expert appraisal</Link>.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><Image className="h-5 w-5 text-emerald-400" /> Condition & Restoration</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Inspect for over-cleaning, touch-ups, paint loss, and surface abrasion. Factor restoration into value.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><Search className="h-5 w-5 text-emerald-400" /> Pricing Research</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Compare recent sales, size, subject, and condition. Track artist-specific trends on the <Link to="/artists" className="text-emerald-300 underline">artists page</Link>.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><BadgeDollarSign className="h-5 w-5 text-emerald-400" /> Where to Buy</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Bid with confidence in our <Link to="/auctions" className="text-emerald-300 underline">live auctions</Link> and explore the <Link to="/artworks" className="text-emerald-300 underline">gallery</Link>.</p>
								</CardContent>
							</Card>

							<Card className="bg-white/5 backdrop-blur-xl border border-white/10">
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-white font-light"><Users className="h-5 w-5 text-emerald-400" /> Work with Experts</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-white/80 font-light">Unsure about a painting? Request an <Link to="/florida-highwaymen-appraisal" className="text-emerald-300 underline">appraisal</Link> for authentication and valuation.</p>
								</CardContent>
							</Card>
						</div>

						<Separator className="my-12 bg-white/20" />
						<div className="text-white/80 font-light">
							<p>More resources: <Link to="/florida-highwaymen" className="text-emerald-300 underline">Movement guide</Link> · <Link to="/artists" className="text-emerald-300 underline">Artists</Link> · <Link to="/auctions" className="text-emerald-300 underline">Auctions</Link> · <Link to="/florida-highwaymen-appraisal" className="text-emerald-300 underline">Appraisal</Link></p>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
};

export default CollectingGuide;











